import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { lastValueFrom, Observable, Subject } from 'rxjs';
import { EditorsService } from '../../../00_modules/editors/services/editors.service';
import { OrderIdAccessor } from '../../../00_modules/core/services/accessors/order-id.accessor';
import { take, takeUntil } from 'rxjs/operators';
import { AppContextFactory } from '../../../00_modules/contextbar/services/app-context/app-context.factory';
import { ContentHostingContext } from '../../../00_modules/contextbar/services/app-context';
import { NotificationFacade } from '../../../store/facade/notification.facade';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'je-sub-header',
    imports: [CommonModule, TranslateModule],
    templateUrl: './sub-header.component.html',
})
export class SubHeaderComponent implements OnInit, OnDestroy {
    public editors$?: Observable<string[]>;
    public showEditors: boolean = false;

    private destroy$: Subject<void> = new Subject<void>();
    private activeOrderId: number | null = null;
    private readonly appContext$ = this.appContextFactory.context$.pipe(takeUntil(this.destroy$));

    constructor(
        private editorsService: EditorsService,
        private orderIdAccessor: OrderIdAccessor,
        private appContextFactory: AppContextFactory,
        private notificationFacade: NotificationFacade,
    ) {}

    public ngOnInit(): void {
        this.appContext$.subscribe(context => {
            void this.handleContextChange(context.hostingContext);
        });
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        void this.releaseIfNeeded();
    }

    private async handleContextChange(hostingContext: ContentHostingContext): Promise<void> {
        if (hostingContext === ContentHostingContext.Order || hostingContext === ContentHostingContext.ReadonlyOrder) {
            await this.enterOrderContext();
            return;
        }

        await this.leaveOrderContext();
    }

    private async enterOrderContext(): Promise<void> {
        this.showEditors = true;
        const orderId = await this.orderIdAccessor.getOrderIdAsync();

        // Avoid re-initializing the same editor session when context emits repeatedly.
        if (!orderId || this.activeOrderId === orderId) {
            return;
        }

        await this.releaseIfNeeded();
        this.editors$ = await this.editorsService.init(orderId);
        this.activeOrderId = orderId;
    }

    private async leaveOrderContext(): Promise<void> {
        await this.releaseIfNeeded();
        this.showEditors = false;
        this.editors$ = undefined;
        this.activeOrderId = null;
    }

    private async releaseIfNeeded(): Promise<void> {
        const inEdit = await lastValueFrom(this.notificationFacade.orderInEditMode$.pipe(take(1)));

        if (!inEdit?.orderId || !inEdit.user) {
            return;
        }

        await this.editorsService.release();
    }
}
